import { AppModal } from '@Common/AppModal';
import { Spinner } from '@Common/Spinner';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { defaultToastProps } from 'appConstants';
import { SetStateType } from 'commonTypes';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import {
  useGetPrices,
  useGetSubscription,
  useRenewSubscription,
  useUpdateSubscription,
} from 'store/subscription/actions';
import {
  SubscriptionCustomerType,
  SubscriptionPriceType,
} from 'store/subscription/types';

import { ChooseUpdateSubscriptionPrice } from '../UI/ChooseUpdateSubscriptionPrice';
import { UpdateSubscriptionStep } from '../UI/UpdateSubscriptionStep';

interface UpgradeSubscriptionModalProps {
  open: boolean;
  setOpen: SetStateType<boolean>;
  isCanceled?: boolean;
  subscription?: SubscriptionCustomerType;
  setOpenCancelSubscriptionModal: SetStateType<boolean>;
}

export const UpgradeSubscriptionModal = ({
  open,
  setOpen,
  isCanceled = false,
  subscription,
  setOpenCancelSubscriptionModal,
}: UpgradeSubscriptionModalProps) => {
  const modalClose = () => {
    setOpen(false);
  };
  const [activeStep, setActiveStep] = useState(1);
  const [chosenPrice, setChosenPrice] = useState<SubscriptionPriceType | null>(null);
  const { mutate: updateSubscription, isPending: isUpdatePending } =
    useUpdateSubscription();
  const { mutate: renewSubscription, isPending: isRenewPending } = useRenewSubscription();
  const { data: prices } = useGetPrices();
  const [activePrice, setActivePrice] = useState<SubscriptionPriceType | undefined>();
  const { data: customer } = useGetSubscription();

  useEffect(() => {
    if (prices) {
      const newActivePrice = prices?.find(
        (price) => price.price_id === subscription?.current_price_id,
      );
      setActivePrice(newActivePrice);
    }
  }, [prices]);

  return (
    <AppModal open={open} size="max-w-6xl">
      <div className="flex items-center justify-between w-full mb-3">
        <h1 className="text-xl">
          <FormattedMessage id={isCanceled ? 'choosePlan' : 'updatePlan'} />
        </h1>
        <XMarkIcon
          onClick={() => {
            setOpen(false);
          }}
          className="h-6 w-6 cursor-pointer text-gray-500 md:hover:text-gray-700"
        />
      </div>
      {activeStep === 1 && (
        <ChooseUpdateSubscriptionPrice
          chosenPrice={chosenPrice}
          setChosenPrice={setChosenPrice}
          active={activePrice?.price_id || ''}
          subscription={subscription}
          modalClose={modalClose}
          setOpenCancelSubscriptionModal={setOpenCancelSubscriptionModal}
        />
      )}
      {activeStep === 2 && (
        <UpdateSubscriptionStep price={chosenPrice} activePrice={activePrice} />
      )}

      <div className="w-full flex items-center justify-end space-x-3 mt-4">
        <button
          disabled={isUpdatePending || isRenewPending}
          onClick={() => {
            if (activeStep === 1) {
              setOpen(false);
            } else {
              setActiveStep(1);
            }
          }}
          type="button"
          className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          <FormattedMessage id={activeStep === 1 ? 'cancel' : 'previous'} />
        </button>
        <button
          onClick={() => {
            if (!chosenPrice) {
              toast('Please select a new plan', {
                ...defaultToastProps,
                type: 'warning',
              });
              return;
            }
            if (activeStep === 1) {
              setActiveStep(2);
            } else {
              if (!isCanceled) {
                updateSubscription(chosenPrice.price_id, {
                  onSuccess: modalClose,
                });
              } else {
                renewSubscription(
                  {
                    customer: customer?.id || '',
                    items: [
                      {
                        price: chosenPrice.price_id,
                      },
                    ],
                  },
                  {
                    onSuccess: modalClose,
                  },
                );
              }
            }
          }}
          type="button"
          className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          {isUpdatePending || isRenewPending ? (
            <Spinner />
          ) : (
            <FormattedMessage id={activeStep === 1 ? 'next' : 'save'} />
          )}
        </button>
      </div>
    </AppModal>
  );
};
